import * as Color from "color"
import { TableRow, TableFormattingOptions, ChartBarDataset } from "pptxgenjs"
import {
	Data as PpiGeneralData,
	DataGroup,
	DataRow,
	ValuesType
} from "./ppi-general-table"
import { TableConfiguration } from "../../configuration"
import defineRows, {
	CallbackRow,
	Row,
	ComponentContainerRow,
	Rows
} from "../../../ppi/ppi-detail/table-rows"
import TableContainer from "../../../ppi/ppi-detail/investments/table-container"
import investmentsHelper, {
	ProjectRow,
	reduceThemeProjectAmounts
} from "../../../ppi/ppi-detail/utils/investments-helper"
import * as CashFlowDomain from "../../../../domain/sfp/cash-flow-domain"
import * as CounterDomain from "../../../../domain/sfp/counter-domain"
import * as Model from "../../../../model"

export function createPpiGeneralData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,
	priorities: number[]
): PpiGeneralData {
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	const expenseRows = getExpenseRows(rows, ppi)
	const fctvaRows = getFctvaRows(rows)
	const projectsRows = getProjectsRows(ppi, startYear, priorities)
	const ratiosRows = getRatiosRows(rows)
	const receiptsRows = getReceiptsRows(rows, ppi)

	return {
		expense: createDataGroup(
			"Dépenses",
			expenseRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities,
			rows.TOTAL_SPENDING
		),
		fctva: createDataGroup(
			"FCTVA",
			fctvaRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities
		),
		ratios: createDataGroup(
			"Ratios",
			ratiosRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities
		),
		receipts: createDataGroup(
			"Recettes",
			receiptsRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities,
			rows.TOTAL_REVENUE
		),
		totalResult: createTotalResultDataRow(
			rows.ANNUAL_PPI_TOTAL,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			priorities
		)
	}
}

export function createPpiResultantData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,
	priorities:number[]
	){
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	return {
		totalResult: createTotalResultDataRow(
			rows.ANNUAL_PPI_TOTAL,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			priorities
		)
	}
}
export function createPpiRatioData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,
	priorities: number[]
){
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	const projectsRows = getProjectsRows(ppi, startYear, priorities)
	const ratiosRows = getRatiosRows(rows)
	return {
		ratios: createDataGroup(
			"Ratios",
			ratiosRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities
		)
	}
}
export function createPpiDispensesData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,
	priorities: number[]
	){
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	const expenseRows = getExpenseRows(rows, ppi)
	const projectsRows = getProjectsRows(ppi, startYear,priorities)

	return {
		expense: createDataGroup(
			"Dépenses",
			expenseRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities,
			rows.TOTAL_SPENDING
		),
		
	}
}
export function createPpiRecettesData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,
	priorities: number[]
){
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	const projectsRows = getProjectsRows(ppi, startYear,priorities)
	const receiptsRows = getReceiptsRows(rows, ppi)

	return {
		
		receipts: createDataGroup(
			"Recettes",
			receiptsRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities,
			rows.TOTAL_REVENUE
		)
	}
}


export function createPpiFctvaData(
	la: Model.LocalAuthority,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	scenario: Model.Scenario,
	startYear: number,	
	priorities: number[]
){
	const rows = defineRows(ppi, false, startYear, [], la, scenario)
	const fctvaRows = getFctvaRows(rows)
	const projectsRows = getProjectsRows(ppi, startYear,priorities)

	return {
		fctva: createDataGroup(
			"FCTVA",
			fctvaRows,
			ppiYears,
			ppi,
			ppiYearLoans,
			startYear,
			scenario,
			la,
			projectsRows,
			priorities
		),
	}
}

export function createRatioChartData(
	service: Model.Service,
	records: Model.SituationRecord[],
	scenario: Model.Scenario,
	dayOfYear: number,
	daysInYear: number
): ChartBarDataset[] {
	const labels: string[] = [
		"Total dépense",
		"Référentiel",
		"Budget annuel",
		"Budget disponible"
	]
	const name = "Avancement budgétaire"
	const annualBudget = CounterDomain.getAnnualBudget(service, scenario)
	const serviceRecords = CounterDomain.filterRecordsByService(records, service)
	const { engaged, liquidated } = CounterDomain.sumBudgets(serviceRecords)
	const totalExpense = CounterDomain.getTotalExpense(engaged, liquidated)
	const planned = service.plannedConsumption || 1
	const repository = CounterDomain.getRepository(annualBudget, planned, dayOfYear, daysInYear)
	const available = CounterDomain.getAvailableBudget(annualBudget, totalExpense)
	const values = [totalExpense, repository, annualBudget, available]

	return [{ labels, name, values }]
}

export function createRatioTableData(
	service: Model.Service,
	records: Model.SituationRecord[],
	scenario: Model.Scenario,
	dayOfYear: number,
	daysInYear: number,
	tableConf: TableConfiguration
): TableRow[] {
	const cellCommonOptions = getCellCommonOptions(tableConf)
	const cellHeaderOptions = getCellHeaderOptions(tableConf)
	const cellNumericOptions = getCellNumericOptions()
	const cellTotalOptions = getCellTotalOptions(tableConf)

	const annualBudget = CounterDomain.getAnnualBudget(service, scenario)
	const serviceRecords = CounterDomain.filterRecordsByService(records, service)
	const { engaged, liquidated } = CounterDomain.sumBudgets(serviceRecords)
	const totalExpense = CounterDomain.getTotalExpense(engaged, liquidated)
	const planned = service.plannedConsumption || 1
	const repository = CounterDomain.getRepository(annualBudget, planned, dayOfYear, daysInYear)

	const euroformatter = new Intl.NumberFormat(
		navigator.language,
		{ currency: "EUR", style: "currency" }
	)

	const percentformatter = new Intl.NumberFormat(
		navigator.language,
		{ style: "percent" }
	)

	return [
		[
			{ text: "Avancement budgétaire", options: cellHeaderOptions },
			{ text: `${dayOfYear}/${daysInYear}`, options: cellCommonOptions }
		],
		[
			{ text: "Budget annuel", options: cellHeaderOptions },
			{
				text: euroformatter.format(annualBudget),
				options: {...cellCommonOptions, ...cellNumericOptions }
			}
		],
		[
			{ text: "Consommation prévue", options: cellHeaderOptions },
			{
				text: percentformatter.format(planned),
				options: {...cellCommonOptions, ...cellNumericOptions }
			}
		],
		[
			{ text: "Référentiel", options: cellHeaderOptions },
			{
				text: euroformatter.format(repository),
				options: {...cellCommonOptions, ...cellNumericOptions }
			}
		],
		[
			{ text: "Dépenses engagées", options: cellHeaderOptions },
			{
				text: euroformatter.format(engaged),
				options: {...cellCommonOptions, ...cellNumericOptions }
			}
		],
		[
			{ text: "Dépenses liquidées", options: cellHeaderOptions },
			{
				text: euroformatter.format(liquidated),
				options: {...cellCommonOptions, ...cellNumericOptions }
			}
		],
		[
			{ text: "Total dépenses", options: cellHeaderOptions },
			{
				text: euroformatter.format(totalExpense),
				options: { ...cellTotalOptions, ...cellNumericOptions}
			}
		],
	]
}

function createDataGroup(
	title: string,
	rows: Row[],
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	startYear: number,
	scenario: Model.Scenario,
	localAuthority: Model.LocalAuthority,
	projectsRows: ProjectRow[],
	priorities: number[],
	totalRow?: Row
): DataGroup {
	const initialDataGroup: DataGroup = {
		data: [] as DataRow[],
		title
	}

	if (totalRow) initialDataGroup.total = {
		label: totalRow.title,
		values: []
	}

	const dataGroup = rows.reduce<DataGroup>((dataGroup, row) => {
		const dataRow: DataRow = {
			label: row.title,
			values: [],
			valuesType: "FCTVA" === row.key || "DEBT_REFUND_PERCENTILE" === row.key
				? ValuesType.Percentage
				: ValuesType.Currency
		}

		if (isComponentContainerRow(row)) {
			// c'est dégueulasse mais je ne sais pas comment faire autrement
			if ((row.rowComponent.component as any) === (TableContainer as any)) {
				const subDataGroup: DataGroup = { data: [], title: "Investissements" }
				for (const projectRow of projectsRows) {
					if (!projectRow.subtheme && !projectRow.project) {
						const subDataRow: DataRow = {
							label: projectRow.title,
							values: reduceThemeProjectAmounts(
								projectRow.theme,
								ppi,
								startYear,
								priorities
							),
							valuesType: ValuesType.Currency
						}

						subDataGroup.data.push(subDataRow)
					}
				}

				dataGroup.data.push(subDataGroup)
			}
		} else {
			for (const ppiYear of ppiYears) {
				if (isCallbackRow(row)) {
					const value = row.func(
						ppiYear,
						ppi,
						ppiYearLoans,
						priorities,
						startYear,
						scenario,
						localAuthority
					)

					if ("CAF_DEVELARGING" === row.key) {
						dataRow.valuesType = undefined
						dataRow.values.push(CashFlowDomain.formatDeleveragingYears(value))
					} else dataRow.values.push(value || 0)
				} else if (row.mapping) {
					const value = row.mapping[ppiYear.fctvaRetrieve || ppi.fctvaRetrieve]
					dataRow.values.push(value)
				} else {
					const value = ppiYear[row.property] || ppi[row.property] || 0
					dataRow.values.push(value)
				}
			}

			dataGroup.data.push(dataRow)
		}

		return dataGroup
	}, initialDataGroup)

	if (totalRow) {
		for (const ppiYear of ppiYears) {
			const value = isCallbackRow(totalRow) && totalRow.func(
				ppiYear,
				ppi,
				ppiYearLoans,
				[],
				startYear,
				scenario,
				localAuthority
			)

			dataGroup.total.values.push(value || 0)
		}
	}

	return dataGroup
}

function createTotalResultDataRow(
	row: CallbackRow,
	ppiYears: Model.PpiYear[],
	ppi: Model.Ppi,
	ppiYearLoans: Model.PpiYearLoan[],
	startYear: number,
	scenario: Model.Scenario,
	localAuthority: Model.LocalAuthority,
	priorities:number[]
): DataRow {
	return ppiYears.reduce<DataRow>((dataRow, ppiYear) => {
		dataRow.values.push(row.func(
			ppiYear,
			ppi,
			ppiYearLoans, 
			priorities,
			startYear,
			scenario,
			localAuthority
		))
		return dataRow
	}, { label: row.title, values: [], valuesType: ValuesType.Currency })
}

function getCellCommonOptions(
	tableConf: TableConfiguration
): TableFormattingOptions {
	const borderColor = Color(tableConf.common.borderColor).hex().substr(1)
	const borderWidthConf = tableConf.common.borderWidth as string
	const borderWidth = parseFloat(borderWidthConf.substr(
		0, borderWidthConf.length - 2))

	return {
		border: { color: borderColor, pt: borderWidth },
		color: Color(tableConf.common.color).hex().substr(1),
		fontFace: tableConf.common.fontFamily,
		fontSize: tableConf.common.fontSize as number,
		margin: 1
	}
}

function getCellHeaderOptions(
	tableConf: TableConfiguration
): TableFormattingOptions {
	return {
		...getCellCommonOptions(tableConf),
		bold: true,
		color: Color(tableConf.header.color).hex().substr(1),
		fill: Color(tableConf.header.backgroundColor).hex().substr(1)
	}
}

function getCellNumericOptions(): TableFormattingOptions {
	return { align: "right" }
}

function getCellTotalOptions(
	tableConf: TableConfiguration
): TableFormattingOptions {
	return {
		...getCellCommonOptions(tableConf),
		align: "right",
		bold: true,
		color: Color(tableConf.header.color).hex().substr(1),
		fill: Color(tableConf.header.backgroundColor).hex().substr(1)
	}
}

function getExpenseRows(rows: Rows, ppi: Model.Ppi): Row[] {
	const ppiExpenseRows = ppi.expenses.map<CallbackRow>(expense => {
		return {
			cellProps: null,
			key: null,
			title: expense.name,
			func(ppiYear) {
				const expenseYear = expense.years.find(eY => eY.year === ppiYear.year)
				return expenseYear ? expenseYear.value : null
			}
		}
	})

	return [
		rows.DEFICIT_RECOVERY,
		rows.LOAN_REFUND,
		rows.INVESTMENTS,
		rows.TOTAL_INVESTMENT_FCTVA,
		rows.TOTAL_INVESTMENT_NO_FCTVA,
		rows.TOTAL_INVESTMENT,
		rows.SPENDING_THIRD_PARTIES_OPERATION,
		...ppiExpenseRows
	]
}

function getFctvaRows(rows: Rows): Row[] {
	return [
		rows.FCTVA,
		rows.FCTVA_RETRIEVE
	]
}

function getProjectsRows(ppi: Model.Ppi, startYear: number, priorities: number[]) {
	return investmentsHelper(ppi, ppi.investment_themes, priorities, startYear)
}

function getRatiosRows(rows: Rows): Row[] {
	return [
		rows.BRUTE_CAF,
		rows.CRD,
		rows.CAF_DEVELERAGING,
		rows.DEBT_REFUND_PERCENTILE,
		rows.INVESTMENT_NET_CAF
	]
}

function getReceiptsRows(rows: Rows, ppi: Model.Ppi): Row[] {
	const ppiReceiptsRows = ppi.revenues.map<CallbackRow>(receipts => {
		return {
			cellProps: null,
			key: null,
			title: receipts.name,
			func(ppiYear) {
				const receiptsYear = receipts.years.find(eY => eY.year === ppiYear.year)
				return receiptsYear ? receiptsYear.value : null
			}
		}
	})

	return [
		rows.PREVIOUS_SURPLUS,
		rows.SELF_FINANCING,
		rows.FCTVA_VALUE,
		rows.PLANNING_FEE,
		rows.AWARDED_GRANT,
		rows.ASKED_GRANT,
		rows.ASKED_GRANT_DETAIL,
		rows.LOANS,
		rows.SALE_OF_HERITAGE,
		rows.REVENUE_THIRD_PARTIES_OPERATION,
		...ppiReceiptsRows
	]
}

function isComponentContainerRow(row: Row): row is ComponentContainerRow {
	return undefined !== (row as ComponentContainerRow).rowComponent
}

function isCallbackRow(row: Row): row is CallbackRow {
	return undefined !== (row as CallbackRow).func
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/ppi-generator/utils.ts